// src/components/dashboard/Dashboard.js
import React from 'react';
import { useApp } from '../../contexts/AppContext';
import { StatCard, Card, Badge, FuelBar, ResponsiveGrid } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';
import { TRUCK_STATUSES } from '../../data/store';

export default function Dashboard() {
  const { trucks, users, clients, gpsDevices, documents, currentUser } = useApp();
  const { isMobile } = useResponsive();

  const movingTrucks = trucks.filter(t => t.status === 'moving').length;
  const assignedGPS = gpsDevices.filter(g => g.status === 'assigned').length;
  const pendingDocs = documents.filter(d => d.status === 'pending').length;

  const recentTrucks = [...trucks].sort(() => Math.random() - 0.5).slice(0, 5);

  const stats = currentUser?.role === 'client'
    ? [
        { label: 'My Trucks', value: trucks.filter(t => t.clientId === currentUser.clientId).length, icon: '🚛', color: '#3b82f6' },
        { label: 'Moving', value: trucks.filter(t => t.clientId === currentUser.clientId && t.status === 'moving').length, icon: '▶', color: '#10b981' },
        { label: 'Idle', value: trucks.filter(t => t.clientId === currentUser.clientId && t.status === 'idle').length, icon: '⏸', color: '#f59e0b' },
      ]
    : [
        { label: 'Total Trucks', value: trucks.length, icon: '🚛', color: '#3b82f6', trend: 12 },
        { label: 'Moving', value: movingTrucks, icon: '▶', color: '#10b981', trend: 5 },
        { label: 'GPS Devices', value: `${assignedGPS}/${gpsDevices.length}`, icon: '📡', color: '#8b5cf6', trend: 0 },
        { label: 'Pending Docs', value: pendingDocs, icon: '📄', color: '#f97316', trend: -8 },
        ...(currentUser?.role === 'admin' ? [
          { label: 'Active Clients', value: clients.filter(c => c.status === 'active').length, icon: '🏢', color: '#f59e0b', trend: 3 },
          { label: 'Total Users', value: users.filter(u => u.status === 'active').length, icon: '👥', color: '#ec4899', trend: 1 },
        ] : []),
      ];

  return (
    <div style={styles.page}>
      <ResponsiveGrid columns={Math.min(stats.length, 4)} tabletColumns={2} mobileColumns={2} gap={isMobile ? 10 : 16}>
        {stats.map((s, i) => <StatCard key={i} {...s} />)}
      </ResponsiveGrid>

      <div style={{ ...styles.row, flexDirection: isMobile ? 'column' : 'row' }}>
        <div style={{ flex: 2, width: '100%', minWidth: 0 }}>
          <Card title="Fleet Status Overview" action={!isMobile && (
            <div style={styles.legend}>
              {Object.entries(TRUCK_STATUSES).map(([k, v]) => (
                <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: v.color }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: v.color }} />
                  {v.label}
                </span>
              ))}
            </div>
          )}>
            <div style={styles.truckList}>
              {recentTrucks.map(truck => {
                const status = TRUCK_STATUSES[truck.status] || TRUCK_STATUSES.offline;
                const gps = gpsDevices.find(g => g.id === truck.gpsDeviceId);

                if (isMobile) {
                  return (
                    <div key={truck.id} style={styles.truckCardMobile}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <div style={{ ...styles.truckDot, background: truck.color || '#3b82f6' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.truckPlate}>{truck.plateNumber}</div>
                          <div style={styles.truckModel}>{truck.model} · {truck.driver}</div>
                        </div>
                        <Badge color={status.color} bg={status.bg}>{status.label}</Badge>
                      </div>
                      <FuelBar level={truck.fuelLevel} />
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                        <span style={{ fontSize: 11, color: 'var(--textMuted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{truck.location?.address || '—'}</span>
                        {gps && <Badge color="#8b5cf6">{gps.deviceId}</Badge>}
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={truck.id} style={styles.truckRow}>
                    <div style={{ ...styles.truckDot, background: truck.color || '#3b82f6' }} />
                    <div style={styles.truckInfo}>
                      <div style={styles.truckPlate}>{truck.plateNumber}</div>
                      <div style={styles.truckModel}>{truck.model} · {truck.driver}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 80 }}>
                      <FuelBar level={truck.fuelLevel} />
                    </div>
                    <div style={styles.truckLocation}>{truck.location?.address || '—'}</div>
                    {gps && <Badge color="#8b5cf6">{gps.deviceId}</Badge>}
                    <Badge color={status.color} bg={status.bg}>{status.label}</Badge>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        <div style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card title="GPS Status">
            <div style={styles.gpsStats}>
              {[
                { label: 'Assigned', count: gpsDevices.filter(g => g.status === 'assigned').length, color: '#10b981' },
                { label: 'Available', count: gpsDevices.filter(g => g.status === 'available').length, color: '#3b82f6' },
                { label: 'Maintenance', count: gpsDevices.filter(g => g.status === 'maintenance').length, color: '#f59e0b' },
              ].map(item => (
                <div key={item.label} style={styles.gpsRow}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color }} />
                    <span style={{ fontSize: 12, color: 'var(--textSecondary)' }}>{item.label}</span>
                  </div>
                  <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 18, color: item.color }}>{item.count}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Recent Documents">
            {documents.slice(0, 4).map(doc => (
              <div key={doc.id} style={styles.docRow}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--textSecondary)' }}>{doc.docNumber}</div>
                  <div style={{ fontSize: 11, color: 'var(--textFaint)' }}>{doc.type}</div>
                </div>
                <Badge
                  color={doc.status === 'delivered' ? '#10b981' : doc.status === 'in-transit' ? '#3b82f6' : '#f59e0b'}
                >
                  {doc.status}
                </Badge>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { display: 'flex', flexDirection: 'column', gap: 20 },
  row: { display: 'flex', gap: 16, alignItems: 'flex-start' },
  legend: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  truckList: { display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 },
  truckRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' },
  truckCardMobile: { padding: '12px', borderRadius: 10, background: 'var(--bgInput)', border: '1px solid var(--border)', marginBottom: 8 },
  truckDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  truckInfo: { minWidth: 140 },
  truckPlate: { fontSize: 13, fontWeight: 600, color: 'var(--textPrimary)', fontFamily: 'Syne, sans-serif' },
  truckModel: { fontSize: 11, color: 'var(--textFaint)', marginTop: 1 },
  truckLocation: { fontSize: 11, color: 'var(--textMuted)', flex: 1, textAlign: 'right', marginRight: 8 },
  gpsStats: { display: 'flex', flexDirection: 'column', gap: 14, marginTop: 4 },
  gpsRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  docRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' },
};
